<template>
    <PublicProfileLayout :role="role" :inner-usage="innerUseage">

        <div class="w-full grid align-items-center justify-content-center px-4">

            <div class="flex flex-column w-full md:w-6 lg:w-8 justify-content-center align-items-center">
                <div class="back-section flex align-self-start pt-6">
                    <Button icon="pi pi-arrow-left" label="Back" class="p-button-text Caption back-button mb-2 "
                        @click="goBack" />
                </div>
                <div class="col-12 flex flex-column justify-content-center align-items-center">
                    <h2 class="H6-Medium ">Write a Review</h2>
                </div>
                <img :src="studentAvatar" alt="Student Avatar"
                    class="border-circle mb-3" style="width: 100px; height: 100px; object-fit: cover;" />

                <div class="mb-3">
                    <div class="Paragraph-Medium text-center">{{ studentName }}</div>
                    <div class="Label text-center">{{ studentGrade }}</div>
                </div>

                <Rating v-model="rating" :cancel="false" :stars="5" class="py-2 custom-rating mb-1"
                    @update:modelValue="errors.rating = ''" />
                <small class="p-error block text-center mb-3 field-error">{{ errors.rating }}</small>

                <div class="w-full text-left">
                    <label class="Label block mb-3">Tutor Name</label>
                    <InputText v-model="tutorName" placeholder="Enter tutor name" class="w-full mb-1"
                        :class="{ 'p-invalid': errors.tutorName }" variant="filled" size="small"
                        @update:modelValue="errors.tutorName = ''" />
                    <small class="p-error block mb-3 field-error">{{ errors.tutorName }}</small>
                </div>

                <div class="w-full text-left">
                    <label class="Label block mb-3">Review</label>
                    <Textarea v-model="reviewText" rows="4" autoResize placeholder="Write your review..."
                        class="w-full mb-1" :class="{ 'p-invalid': errors.review }" variant="filled" size="small"
                        @update:modelValue="errors.review = ''" />
                    <small class="p-error block mb-3 field-error">{{ errors.review }}</small>
                </div>

                <div class="flex justify-content-center gap-3 mt-3 mb-5">
                    <Button label="Submit" class="orange-button" :loading="submitting" @click="submitReview" />
                    <Button label="Cancel" class="orange-button-transparent" @click="goBack" />
                </div>
            </div>
        </div>
    </PublicProfileLayout>
</template>
<script setup>
import PublicProfileLayout from "@/layout/PublicProfileLayout.vue";
import { defineProps, ref, reactive, computed, defineEmits, onMounted } from "vue";
import Rating from "primevue/rating";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import Button from 'primevue/button';
import { createReview } from "@/api/student";
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { useAuthStore } from "@/stores/authStore";
import { cleanupLables } from "@/utils/cleanups";
import blankProfile from '@/assets/blank-profile.png';

defineProps({
    innerUseage: {
        type: Boolean,
        default: false,
        required: false
    },
    tutor: {
        type: String,
        required: false
    }
});

defineEmits(["submit-review"]);

const toast = useToast();
const route = useRoute();
const router = useRouter();
const tutorId = route.query.tutorId;
const tutorName = ref(route.query.tutorName || "");
const role = ref("student");
const authStore = useAuthStore();
const studentName = computed(() => authStore.user?.username || "");
const studentGrade = computed(() => cleanupLables(authStore.user?.currentEducationLevel) || "");
const studentAvatar = computed(() => authStore.profileImageUrl || blankProfile);

onMounted(() => {
    authStore.loadProfileImage();
});
const rating = ref(0);
const reviewText = ref("");
const submitting = ref(false);

const errors = reactive({
    rating: "",
    tutorName: "",
    review: ""
});

const validate = () => {
    errors.rating = !rating.value ? "Please select a rating" : "";
    errors.tutorName = !tutorName.value.trim() ? "Tutor name is required" : "";
    errors.review = !reviewText.value.trim() ? "Review is required" : "";
    return !errors.rating && !errors.tutorName && !errors.review;
};

const submitReview = async () => {
    if (!validate()) return;

    submitting.value = true;
    try {
        await createReview({
            rating: rating.value,
            tutorId: tutorId,
            review: reviewText.value.trim()
        });
        toast.add({
            severity: 'success',
            summary: 'Review Submitted',
            detail: 'Your review has been submitted successfully.',
            life: 3000
        });
        goBack();
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: 'Submission Failed',
            detail: error.response?.data?.message || 'An error occurred while submitting your review.',
            life: 4000
        });
    } finally {
        submitting.value = false;
    }
};
const goBack = () => {
    router.back();
};
</script>
<style scoped>
::v-deep(.p-rating-icon) {
    color: #facc15 !important;
    transform: scale(1.8);
    width: 50px;
}

.field-error {
    min-height: 1rem;
    line-height: 1rem;
    font-size: 0.75rem;
    color: red;
}
</style>